// src/components/CreditsAndAdjustments.js
import React, { useEffect } from 'react';
import { Sliders, HelpCircle, AlertTriangle, Percent, DollarSign, CheckCircle } from 'react-feather';

function CreditsAndAdjustments({ data, onDataChange, totals }) {
  
  // Helper for boolean radio buttons
  const handleToggleChange = (e) => {
    const { name, value } = e.target;
    onDataChange({
      target: { name, value: value === 'true', type: 'radio' }
    });
  };

  // --- AUTO-CALCULATE IRA TAX PENALTY ---
  useEffect(() => {
    const earlyAmount = parseFloat(data.iraEarlyDistAmount) || 0;
    const exceptions = parseFloat(data.iraExceptionAmount) || 0;
    const taxableAmount = Math.max(0, earlyAmount - exceptions);
    const calculatedTax = taxableAmount * 0.10;

    if (calculatedTax !== parseFloat(data.taxOnIras)) {
      onDataChange({
        target: {
          name: 'taxOnIras',
          value: calculatedTax
        }
      });
    }
  }, [data.iraEarlyDistAmount, data.iraExceptionAmount, data.taxOnIras, onDataChange]);

  return (
    <div className="form-section">
      <div className="content-box-header">
        <h2><Sliders size={20} className="icon-orange" /> Adjustments, Credits & Other Taxes</h2>
      </div>
      
      {/* =================================================================================
          PART 1: DEDUCTIONS & ADJUSTMENTS (Schedule 1 Part II)
         ================================================================================= */}

      {/* --- Form 1040 Line 16 Specific Taxes --- */}
        <div className="dependent-card" style={{ marginBottom: '1.5rem', backgroundColor: '#fff', border: '1px solid #f0e7e2ff' }}>
          <h5 style={{ margin: '0 0 1rem 0', fontSize: '0.95rem', color: '#695547ff' }}>Taxes on Form 1040, Line 16</h5>
          
          {/* Box 1: Form 8814 */}
          <div className="form-row">
            <div className="form-group checkbox-group">
              <input type="checkbox" id="form8814" name="form8814" checked={data.form8814 || false} onChange={onDataChange} />
              <label htmlFor="form8814">1. Form 8814 (Child's Interest/Dividends)</label>
            </div>
            {data.form8814 && (
              <div className="form-group">
                <input type="number" name="taxForm8814Amount" value={data.taxForm8814Amount ?? ''} onChange={onDataChange} placeholder="Amount" />
              </div>
            )}
          </div>

          {/* Box 2: Form 4972 */}
          <div className="form-row">
            <div className="form-group checkbox-group">
              <input type="checkbox" id="form4972" name="form4972" checked={data.form4972 || false} onChange={onDataChange} />
              <label htmlFor="form4972">2. Form 4972 (Lump-Sum Distributions)</label>
            </div>
            {data.form4972 && (
              <div className="form-group">
                <input type="number" name="taxForm4972Amount" value={data.taxForm4972Amount ?? ''} onChange={onDataChange} placeholder="Amount" />
              </div>
            )}
          </div>

          {/* Box 3: Specific Items */}
          <div style={{ marginTop: '1rem', padding: '10px', backgroundColor: '#fcfaf8ff', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
            <label style={{ fontSize: '0.9rem', fontWeight: '600', color: '#554433ff', marginBottom: '0.5rem', display: 'block' }}>
              3. Other Taxes (Check all that apply)
            </label>
            
            <div className="form-row">
                <div className="form-group">
                    <label style={{ fontSize: '0.85rem' }}>Section 962 Election</label>
                    <input type="number" name="taxSection962" value={data.taxSection962 ?? ''} onChange={onDataChange} placeholder="0.00" />
                </div>
                <div className="form-group">
                    <label style={{ fontSize: '0.85rem' }}>Education Credit Recapture</label>
                    <input type="number" name="taxRecaptureEducation" value={data.taxRecaptureEducation ?? ''} onChange={onDataChange} placeholder="0.00" />
                </div>
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label style={{ fontSize: '0.85rem' }}>Form 8621 (1291TAX)</label>
                    <input type="number" name="taxForm8621" value={data.taxForm8621 ?? ''} onChange={onDataChange} placeholder="0.00" />
                </div>
                <div className="form-group">
                    <label style={{ fontSize: '0.85rem' }}>Form 8978 (Partner Audit)</label>
                    <input type="number" name="taxForm8978" value={data.taxForm8978 ?? ''} onChange={onDataChange} placeholder="0.00" />
                </div>
            </div>

            <div className="form-row">
                <div className="form-group">
                    <label style={{ fontSize: '0.85rem' }}>Section 965(i) Trigger</label>
                    <input type="number" name="taxSection965i" value={data.taxSection965i ?? ''} onChange={onDataChange} placeholder="0.00" />
                </div>
                <div className="form-group">
                    <label style={{ fontSize: '0.85rem' }}>Other (Specify Code & Amount)</label>
                    <div style={{ display: 'flex', gap: '5px' }}>
                        <input type="text" name="otherTaxFormNumber" value={data.otherTaxFormNumber ?? ''} onChange={onDataChange} placeholder="Code" style={{ flex: 1 }} />
                        <input type="number" name="otherTaxFormAmount" value={data.otherTaxFormAmount ?? ''} onChange={onDataChange} placeholder="Amount" style={{ flex: 1 }} />
                    </div>
                </div>
            </div>
          </div>

        </div>
      
      <div className="dependent-card">
        <h4 style={{ marginBottom: '1rem', color: '#695b47ff' }}>Adjustments to Income</h4>
        
        <div className="form-row">
          <div className="form-group">
            <label>Traditional IRA Contribution</label>
            <input type="number" name="iraContribution" value={data.iraContribution ?? ''} onChange={onDataChange} placeholder="0" />
          </div>
          <div className="form-group">
            <label>Student Loan Interest</label>
            <input type="number" name="studentLoanInterest" value={data.studentLoanInterest ?? ''} onChange={onDataChange} placeholder="0" />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
              <label>Educator Expenses</label>
              <div className="has-tooltip header-help-icon" data-tooltip="Up to $300 for K-12 teachers ($600 if married filing jointly and both are teachers).">
                <HelpCircle size={16} />
              </div>
            </div>
            <input type="number" name="educatorExpenses" value={data.educatorExpenses ?? ''} onChange={onDataChange} />
          </div>
          <div className="form-group">
            <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
              <label>HSA Deduction</label>
              <div className="has-tooltip header-help-icon" data-tooltip="Enter the deduction amount from Form 8889, Line 13.">
                <HelpCircle size={16} />
              </div>
            </div>
            <input type="number" name="hsaDeduction" value={data.hsaDeduction ?? ''} onChange={onDataChange} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Self-Employed Health Insurance</label>
            <input type="number" name="selfEmployedHealthInsurance" value={data.selfEmployedHealthInsurance ?? ''} onChange={onDataChange} />
          </div>
          <div className="form-group">
            <label>Self-Employed SEP, SIMPLE, etc.</label>
            <input type="number" name="selfEmployedRetirementDeduction" value={data.selfEmployedRetirementDeduction ?? ''} onChange={onDataChange} />
          </div>
        </div>

        {/* --- NEW: Less Common Adjustments --- */}
        <div className="form-row">
          <div className="form-group">
            <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
              <label>Reservist/Artist Expenses</label>
              <div className="has-tooltip header-help-icon" data-tooltip="Certain business expenses of reservists, performing artists, and fee-basis government officials (Form 2106).">
                <HelpCircle size={16} />
              </div>
            </div>
            <input type="number" name="reservistExpenses" value={data.reservistExpenses ?? ''} onChange={onDataChange} placeholder="Line 12" />
          </div>
          <div className="form-group">
            <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
              <label>Moving Expenses (Military)</label>
              <div className="has-tooltip header-help-icon" data-tooltip="For members of the Armed Forces moving due to a military order (Form 3903).">
                <HelpCircle size={16} />
              </div>
            </div>
            <input type="number" name="movingExpenses" value={data.movingExpenses ?? ''} onChange={onDataChange} placeholder="Line 14" />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Archer MSA Deduction</label>
            <input type="number" name="archerMsaDeduction" value={data.archerMsaDeduction ?? ''} onChange={onDataChange} placeholder="Form 8853" />
          </div>
          <div className="form-group">
            <label>Other Adjustments (Line 24z)</label>
            <input type="number" name="otherAdjustments24z" value={data.otherAdjustments24z ?? ''} onChange={onDataChange} placeholder="e.g. Jury duty pay repayment" />
          </div>
        </div>

        <div className="form-group">
          <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
            <label>Penalty on Early Withdrawal of Savings</label>
            <div className="has-tooltip header-help-icon" data-tooltip="Enter penalties charged by your bank for withdrawing funds from a time savings account (like a CD) before maturity. Form 1099-INT, Box 2.">
                <HelpCircle size={16} />
            </div>
          </div>
          <input type="number" name="earlyWithdrawalPenalty" value={data.earlyWithdrawalPenalty ?? ''} onChange={onDataChange} />
        </div>
      </div>

      {/* --- Alimony Paid --- */}
      <div className="dependent-card" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
        <h4 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Alimony Paid</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Amount Paid</label>
            <input type="number" name="alimonyPaid" value={data.alimonyPaid ?? ''} onChange={onDataChange} placeholder="0" />
          </div>
          <div className="form-group">
            <label>Recipient's SSN</label>
            <input type="text" name="alimonyRecipientSSN" value={data.alimonyRecipientSSN ?? ''} onChange={onDataChange} placeholder="XXX-XX-XXXX" />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Date of Original Divorce Agreement</label>
            <input type="date" name="alimonyPaidDivorceDate" value={data.alimonyPaidDivorceDate ?? ''} onChange={onDataChange} />
            <small>Required for Line 19c</small>
          </div>
        </div>
      </div>
      
      {/* --- Saver's Credit Input --- */}
      <div className="form-row">
        <div className="form-group">
          <label>Retirement Contributions (for Saver's Credit)</label>
          <input type="number" name="retirementContributions" value={data.retirementContributions ?? ''} onChange={onDataChange} />
        </div>
        <div className="form-group checkbox-group">
          <input type="checkbox" id="childTaxCredit" name="childTaxCredit" checked={data.childTaxCredit.eligible} onChange={onDataChange} />
          <label htmlFor="childTaxCredit">Eligible for Child Tax Credit?</label>
        </div>
      </div>

      {/* --- NEW: QBI Manual Override --- */}
      <div className="form-group" style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#fcfaf8ff', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}>
        <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
          <label style={{ fontWeight: 600 }}>Qualified Business Income Deduction (Line 13)</label>
          <div className="has-tooltip header-help-icon" data-tooltip="We calculate this automatically based on your business income (20%). If you have a specific amount from Form 8995, enter it here to override.">
            <HelpCircle size={16} />
          </div>
        </div>
        <input 
          type="number" 
          name="manualQBIDeduction" 
          value={data.manualQBIDeduction ?? ''} 
          onChange={onDataChange} 
          placeholder="Auto-calculated (Enter to override)" 
        />
      </div>

      {/* --- NEW: Line 27 Checkboxes --- */}
          <div className="form-group" style={{ marginTop: '1rem', padding: '10px', backgroundColor: '#fcfaf8ff', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
            <label style={{ fontWeight: 600, fontSize: '0.9rem', color: '#554433ff' }}>EIC Exceptions (Line 27)</label>
            <div className="checkbox-group" style={{ marginTop: '0.5rem' }}>
                <input 
                    type="checkbox" 
                    id="eicBox1" 
                    name="eicBox1" 
                    checked={data.eicBox1 || false} 
                    onChange={onDataChange} 
                />
                <label htmlFor="eicBox1">1. Age 19-24 (Born after Jan 2, 1998)</label>
            </div>
            <div className="checkbox-group">
                <input 
                    type="checkbox" 
                    id="eicBox2" 
                    name="eicBox2" 
                    checked={data.eicBox2 || false} 
                    onChange={onDataChange} 
                />
                <label htmlFor="eicBox2">2. Former Foster Youth or Homeless Youth</label>
            </div>
          </div>

      {/* =================================================================================
          PART 2: ADDITIONAL TAXES (Schedule 2 & Form 1040 Line 16)
         ================================================================================= */}
      
      <div style={{ marginTop: '2rem', borderTop: '3px solid #f0e9e2ff', paddingTop: '1.5rem' }}>
        <div className="content-box-header" style={{ marginBottom: '1rem' }}>
          <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <AlertTriangle size={18} className="icon-orange" /> Additional Taxes
          </h4>
        </div>

        {/* --- NEW: Form 1040 Line 16 Specific Taxes --- */}
        <div className="dependent-card" style={{ marginBottom: '1.5rem', backgroundColor: '#fff', border: '1px solid #f0e7e2ff' }}>
          <h5 style={{ margin: '0 0 1rem 0', fontSize: '0.95rem', color: '#695547ff' }}>Taxes on Form 1040, Line 16</h5>
          
          <div className="form-row">
            <div className="form-group checkbox-group">
              <input type="checkbox" id="form8814" name="form8814" checked={data.form8814 || false} onChange={onDataChange} />
              <label htmlFor="form8814">Form 8814 (Child's Interest/Dividends)</label>
            </div>
            {data.form8814 && (
              <div className="form-group">
                <input type="number" name="taxForm8814Amount" value={data.taxForm8814Amount ?? ''} onChange={onDataChange} placeholder="Amount" />
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group checkbox-group">
              <input type="checkbox" id="form4972" name="form4972" checked={data.form4972 || false} onChange={onDataChange} />
              <label htmlFor="form4972">Form 4972 (Lump-Sum Distributions)</label>
            </div>
            {data.form4972 && (
              <div className="form-group">
                <input type="number" name="taxForm4972Amount" value={data.taxForm4972Amount ?? ''} onChange={onDataChange} placeholder="Amount" />
              </div>
            )}
          </div>
        </div>

        {/* --- Schedule 2 Toggle --- */}
        <div className="form-group" style={{ backgroundColor: '#fff7ed', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #ffedd5' }}>
          <label style={{ fontSize: '1rem', fontWeight: '600', color: '#9a3412', marginBottom: '0.75rem', display: 'block' }}>
            Do you owe any other taxes?
          </label>
          <p style={{ fontSize: '0.9rem', color: '#c2410c', marginBottom: '1rem' }}>
            This includes Alternative Minimum Tax (AMT), excess advance premium tax credit repayment, self-employment tax, tax on early IRA distributions, or household employment taxes.
          </p>
          
          <div className="radio-group">
            <input type="radio" id="sched2_no" name="hasSchedule2" value="false" checked={data.hasSchedule2 === false} onChange={handleToggleChange} />
            <label htmlFor="sched2_no">No</label>
            <input type="radio" id="sched2_yes" name="hasSchedule2" value="true" checked={data.hasSchedule2 === true} onChange={handleToggleChange} />
            <label htmlFor="sched2_yes">Yes</label>
          </div>
        </div>

        {data.hasSchedule2 && (
          <div className="dependent-card" style={{ marginTop: '1rem', animation: 'fadeIn 0.3s' }}>
            
            {/* --- SMART CALCULATOR: IRA/401k Tax --- */}
            <div className="dependent-card" style={{ marginBottom: '1.5rem', border: '1px solid #e1d5cbff', backgroundColor: '#fcfaf8ff' }}>
                <div className="content-box-header" style={{ marginBottom: '1rem', borderBottom: 'none', paddingBottom: 0 }}>
                    <h5 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem', margin: 0 }}>
                        <Percent size={16} className="icon-orange" /> 
                        IRA/401(k) Early Distribution Tax Calculator
                    </h5>
                </div>
                
                <div className="form-row">
                    <div className="form-group">
                        <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                            <label>Amount Withdrawn Early</label>
                            <div className="has-tooltip header-help-icon" data-tooltip="Enter the total amount you withdrew from IRAs or retirement plans before age 59½. Form 1099-R, Box 1 (if Code 1 is in Box 7).">
                                <HelpCircle size={16} />
                            </div>
                        </div>
                        <input 
                            type="number" 
                            name="iraEarlyDistAmount" 
                            value={data.iraEarlyDistAmount ?? ''} 
                            onChange={onDataChange} 
                            placeholder="0" 
                        />
                    </div>
                    <div className="form-group">
                        <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                            <label>Exception Amount</label>
                            <div className="has-tooltip header-help-icon" data-tooltip="Enter the amount used for qualified exceptions: Higher education expenses, First-time home purchase (up to $10k), Medical insurance while unemployed, or Total disability.">
                                <HelpCircle size={16} />
                            </div>
                        </div>
                        <input 
                            type="number" 
                            name="iraExceptionAmount" 
                            value={data.iraExceptionAmount ?? ''} 
                            onChange={onDataChange} 
                            placeholder="0" 
                        />
                    </div>
                </div>

                <div className="form-group" style={{ marginTop: '0.5rem' }}>
                    <label style={{ fontWeight: 'bold', color: '#553f33ff', fontSize: '0.9rem' }}>Calculated 10% Additional Tax (Form 5329)</label>
                    <div className="input-with-prefix">
                        <span className="prefix" style={{ backgroundColor: '#f0e9e2ff' }}>$</span>
                        <input 
                            type="number" 
                            name="taxOnIras" 
                            value={data.taxOnIras ?? ''} 
                            readOnly 
                            style={{ backgroundColor: '#f0e9e2ff', fontWeight: 'bold', color: '#2a1d0fff' }} 
                        />
                    </div>
                    <small>This amount will be added to Schedule 2, Line 6.</small>
                </div>
                <div className="form-group checkbox-group" style={{ marginTop: '0.5rem' }}>
                    <input 
                        type="checkbox" 
                        id="suppressForm5329" 
                        name="suppressForm5329" 
                        checked={data.suppressForm5329 || false} 
                        onChange={onDataChange} 
                    />
                    <label htmlFor="suppressForm5329" style={{ fontSize: '0.9rem' }}>
                        Check if Form 5329 is NOT required (Distribution code "1" on 1099-R)
                    </label>
                </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Alternative Minimum Tax (AMT)</label>
                <input type="number" name="amtTax" value={data.amtTax ?? ''} onChange={onDataChange} placeholder="Form 6251" />
              </div>
              <div className="form-group">
                <label>Excess Premium Tax Credit Repayment</label>
                <input type="number" name="excessAptcRepayment" value={data.excessAptcRepayment ?? ''} onChange={onDataChange} placeholder="Form 8962" />
              </div>
            </div>

            {/* --- Schedule 2 Part I Additions --- */}
            <div className="dependent-card" style={{ marginTop: '1rem', backgroundColor: '#fcfaf8ff', border: '1px dashed #e1d8cbff' }}>
                <h5 style={{ margin: '0 0 1rem 0', fontSize: '0.95rem', color: '#695a47ff' }}>Other Tax Additions (Sch 2, Part I)</h5>
                
                <div className="form-row">
                    <div className="form-group">
                        <label>Repayment of New Clean Vehicle Credit</label>
                        <input type="number" name="repayNewCleanVehicle" value={data.repayNewCleanVehicle ?? ''} onChange={onDataChange} placeholder="Form 8936" />
                        <small>Line 1b</small>
                    </div>
                    <div className="form-group">
                        <label>Repayment of Used Clean Vehicle Credit</label>
                        <input type="number" name="repayOldCleanVehicle" value={data.repayOldCleanVehicle ?? ''} onChange={onDataChange} placeholder="Form 8936" />
                        <small>Line 1c</small>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Recapture of Net EPE (Form 4255)</label>
                        <input type="number" name="recaptureEPE" value={data.recaptureEPE ?? ''} onChange={onDataChange} />
                        <small>Line 1d</small>
                    </div>
                    <div className="form-group">
                        <label>Excessive Payments (Form 4255)</label>
                        <input type="number" name="excessivePaymentsEPE" value={data.excessivePaymentsEPE ?? ''} onChange={onDataChange} />
                        <small>Line 1e</small>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label>Other Additions to Tax</label>
                        <input type="number" name="otherAdditionsTax" value={data.otherAdditionsTax ?? ''} onChange={onDataChange} />
                        <small>Line 1y</small>
                    </div>
                    <div className="form-group">
                      <label>Description for "Other Additions"</label>
                      <input 
                          type="text" 
                          name="otherAdditionsTaxDescription" 
                          value={data.otherAdditionsTaxDescription ?? ''} 
                          onChange={onDataChange} 
                          placeholder="e.g. Interest on deferred tax"
                      />
                    </div>
                </div>
            </div>

            <div className="form-row" style={{ marginTop: '1rem' }}>
                <div className="form-group">
                    <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                        <label>Tax on Unreported Tips</label>
                        <div className="has-tooltip header-help-icon" data-tooltip="Enter the tax amount from Form 4137. This applies if you received tips that you didn't report to your employer.">
                            <HelpCircle size={16} />
                        </div>
                    </div>
                    <input 
                        type="number" 
                        name="unreportedTipTax" 
                        value={data.unreportedTipTax ?? ''} 
                        onChange={onDataChange} 
                        placeholder="Form 4137" 
                    />
                    <small>Schedule 2, Line 5</small>
                </div>

                <div className="form-group">
                    <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                        <label>Household Employment Tax</label>
                        <div className="has-tooltip header-help-icon" data-tooltip="Enter tax from Schedule H if you paid a nanny, housekeeper, etc. cash wages of $2,700+ or $1,000+ in any quarter.">
                            <HelpCircle size={16} />
                        </div>
                    </div>
                    <input 
                        type="number" 
                        name="householdEmploymentTax" 
                        value={data.householdEmploymentTax ?? ''} 
                        onChange={onDataChange} 
                        placeholder="Schedule H" 
                    />
                    <small>Schedule 2, Line 9</small>
                </div>
            </div>

            <div className="form-row">
                <div className="form-group">
                    <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                        <label>Net Investment Income Tax</label>
                        <div className="has-tooltip header-help-icon" data-tooltip="Enter tax from Form 8960. Applies if you have investment income AND your MAGI is over $200k (Single) or $250k (Married).">
                            <HelpCircle size={16} />
                        </div>
                    </div>
                    <input 
                        type="number" 
                        name="netInvestmentIncomeTax" 
                        value={data.netInvestmentIncomeTax ?? ''} 
                        onChange={onDataChange} 
                        placeholder="Form 8960" 
                    />
                    <small>Schedule 2, Line 12</small>
                </div>
                 <div className="form-group">
                    <label>Uncollected Tax on Tips/Life Ins. (W-2)</label>
                    <input 
                        type="number" 
                        name="uncollectedW2Tax" 
                        value={data.uncollectedW2Tax ?? ''} 
                        onChange={onDataChange} 
                        placeholder="From W-2 Box 12" 
                    />
                    <small>Schedule 2, Line 13</small>
                </div>
            </div>

            <div className="dependent-card" style={{ marginTop: '1rem', backgroundColor: '#ffffff', border: '1px solid #f0e9e2ff' }}>
                <h5 style={{ margin: '0 0 1rem 0', fontSize: '0.95rem', color: '#695747ff' }}>Installment Sales & Recapture</h5>
                
                <div className="form-row">
                    <div className="form-group">
                        <label>Interest on Installment Sales (Lots/Timeshares)</label>
                        <input 
                            type="number" 
                            name="installmentInterestLots" 
                            value={data.installmentInterestLots ?? ''} 
                            onChange={onDataChange} 
                            placeholder="Sec. 453(l)(3)" 
                        />
                        <small>Line 14</small>
                    </div>
                    <div className="form-group">
                        <label>Interest on Deferred Tax (Above $150k Sales)</label>
                        <input 
                            type="number" 
                            name="installmentInterestDeferred" 
                            value={data.installmentInterestDeferred ?? ''} 
                            onChange={onDataChange} 
                            placeholder="Sec. 453A(c)" 
                        />
                        <small>Line 15</small>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                            <label>Recapture of Low-Income Housing Credit</label>
                            <div className="has-tooltip header-help-icon" data-tooltip="Enter tax from Form 8611. Applies if you disposed of a Low-Income Housing credit investment early.">
                                <HelpCircle size={16} />
                            </div>
                        </div>
                        <input 
                            type="number" 
                            name="recaptureLowIncomeHousing" 
                            value={data.recaptureLowIncomeHousing ?? ''} 
                            onChange={onDataChange} 
                            placeholder="Form 8611" 
                        />
                        <small>Line 16</small>
                    </div>
                </div>
            </div>

            {/* --- NEW: Line 17 (a-z) Other Taxes --- */}
            <div className="dependent-card" style={{ marginTop: '1rem', backgroundColor: '#fff', border: '1px solid #f0e9e2ff' }}>
                <h5 style={{ margin: '0 0 1rem 0', fontSize: '0.95rem', color: '#695947ff' }}>Other Additional Taxes (Line 17)</h5>
                
                <div className="dependent-card">
                  <h5>Line 17a: Recapture of Other Credits</h5>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        <input 
                          type="checkbox" 
                          name="line17a_3468" 
                          checked={data.line17a_3468 ?? false} 
                          onChange={onDataChange} 
                        />
                        Form 3468 (Investment Credit)
                      </label>
                    </div>
                    <div className="form-group">
                      <label>
                        <input 
                          type="checkbox" 
                          name="line17a_NMCR" 
                          checked={data.line17a_NMCR ?? false} 
                          onChange={onDataChange} 
                        />
                        New Markets Credit (Form 8874)
                      </label>
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        <input 
                          type="checkbox" 
                          name="line17a_ECCFR" 
                          checked={data.line17a_ECCFR ?? false} 
                          onChange={onDataChange} 
                        />
                        Employer Childcare (Form 8882)
                      </label>
                    </div>
                    <div className="form-group">
                      <label>Total Amount</label>
                      <input 
                        type="number" 
                        name="line17aAmount" 
                        value={data.line17aAmount ?? ''} 
                        onChange={onDataChange} 
                      />
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Recapture of Federal Mortgage Subsidy</label>
                    <input type="number" name="recaptureMortgageSubsidy" value={data.recaptureMortgageSubsidy ?? ''} onChange={onDataChange} placeholder="Form 8828 (Line 17a)" />
                  </div>
                  <div className="form-group">
                    <label>Tax on Trust Accumulation</label>
                    <input type="number" name="taxOnTrustAccumulation" value={data.taxOnTrustAccumulation ?? ''} onChange={onDataChange} placeholder="Form 4970 (Line 17b)" />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Excise Tax on Insider Stock</label>
                    <input type="number" name="exciseTaxInsiderStock" value={data.exciseTaxInsiderStock ?? ''} onChange={onDataChange} placeholder="Form 7248 (Line 17c)" />
                  </div>
                  <div className="form-group">
                    <label>Golden Parachute Payments</label>
                    <input type="number" name="goldenParachuteTax" value={data.goldenParachuteTax ?? ''} onChange={onDataChange} placeholder="Excise Tax (Line 17k)" />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Look-back Interest (Form 8697)</label>
                    <input type="number" name="lookBackInterest8697" value={data.lookBackInterest8697 ?? ''} onChange={onDataChange} placeholder="Line 17d" />
                  </div>
                  <div className="form-group">
                    <label>Look-back Interest (Form 8866)</label>
                    <input type="number" name="lookBackInterest8866" value={data.lookBackInterest8866 ?? ''} onChange={onDataChange} placeholder="Line 17e" />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Uncollected SS/Medicare (Form 4137)</label>
                    <input type="number" name="uncollectedSocialSecurityMedicare" value={data.uncollectedSocialSecurityMedicare ?? ''} onChange={onDataChange} placeholder="Line 17f" />
                  </div>
                  <div className="form-group">
                    <label>Uncollected SS/RRTA (Form 8919)</label>
                    <input type="number" name="uncollectedSocialSecurityRRTA" value={data.uncollectedSocialSecurityRRTA ?? ''} onChange={onDataChange} placeholder="Line 17g" />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>First-Time Homebuyer Credit Repayment</label>
                    <input type="number" name="repaymentFirstTimeBuyer" value={data.repaymentFirstTimeBuyer ?? ''} onChange={onDataChange} placeholder="Form 5405 (Line 17j)" />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Section 965 Tax (Transition Tax)</label>
                    <input type="number" name="section965Tax" value={data.section965Tax ?? ''} onChange={onDataChange} placeholder="Line 17n" />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Section 951A Tax (GILTI)</label>
                    <input type="number" name="section951aTax" value={data.section951aTax ?? ''} onChange={onDataChange} placeholder="Line 17o" />
                  </div>
                  <div className="form-group">
                    <label>Section 461(l) Excess Business Loss</label>
                    <input type="number" name="section461lExcessLoss" value={data.section461lExcessLoss ?? ''} onChange={onDataChange} placeholder="Line 17p" />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Section 1061(a) Deferred Tax</label>
                    <input type="number" name="deferredTaxSection1061" value={data.deferredTaxSection1061 ?? ''} onChange={onDataChange} placeholder="Line 17q" />
                  </div>
                </div>

                {/* Line 17z: Other Additional Taxes */}
                <div className="dependent-card" style={{ marginTop: '1rem', backgroundColor: '#fff', border: '1px solid #f0e9e2ff' }}>
                  <h5 style={{ margin: '0 0 1rem 0', fontSize: '0.95rem', color: '#695a47ff' }}>
                    Line 17z: Other Additional Taxes
                  </h5>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        <input 
                          type="checkbox" 
                          name="line17z_PWA7210" 
                          checked={data.line17z_PWA7210 ?? false} 
                          onChange={onDataChange} 
                        />
                        Form 7210/4255 line 1c col(p) - PWA7210
                      </label>
                    </div>
                    <div className="form-group">
                      <label>
                        <input 
                          type="checkbox" 
                          name="line17z_PWA8933" 
                          checked={data.line17z_PWA8933 ?? false} 
                          onChange={onDataChange} 
                        />
                        Form 8933/4255 line 2a col(p) - PWA8933
                      </label>
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        <input 
                          type="checkbox" 
                          name="line17z_NPWA" 
                          checked={data.line17z_NPWA ?? false} 
                          onChange={onDataChange} 
                        />
                        Form 4255 col(p) other - NPWA
                      </label>
                    </div>
                    <div className="form-group">
                      <label>
                        <input 
                          type="checkbox" 
                          name="line17z_Form8978" 
                          checked={data.line17z_Form8978 ?? false} 
                          onChange={onDataChange} 
                        />
                        Form 8978 Adjustment
                      </label>
                    </div>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Other (specify)</label>
                      <input 
                        type="text" 
                        name="line17z_other_description" 
                        value={data.line17z_other_description ?? ''} 
                        onChange={onDataChange}
                        placeholder="Description"
                      />
                    </div>
                    <div className="form-group">
                      <label>Total Amount (Line 17z)</label>
                      <input 
                        type="number" 
                        name="line17zAmount" 
                        value={data.line17zAmount ?? ''} 
                        onChange={onDataChange}
                        placeholder="Total of all 17z taxes"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>HSA/MSA Additional Tax</label>
                    <input type="number" name="hsaAdditionalTax" value={data.hsaAdditionalTax ?? ''} onChange={onDataChange} placeholder="Form 8889" />
                  </div>
                </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Additional Medicare Tax</label>
                <input type="number" name="additionalMedicareTax" value={data.additionalMedicareTax ?? ''} onChange={onDataChange} placeholder="Form 8959" />
                <small>If Medicare wages &gt; $200k ($250k MFJ)</small>
              </div>
              <div className="form-group">
                <label>Homebuyer Credit Repayment</label>
                <input type="number" name="homebuyerCreditRepayment" value={data.homebuyerCreditRepayment ?? ''} onChange={onDataChange} placeholder="Form 5405" />
              </div>
            </div>

            <div className="form-row" style={{ marginTop: '1rem' }}>
              <div className="form-group">
                <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                  <label>Additional Tax</label>
                  <div className="has-tooltip header-help-icon" data-tooltip="Enter any additional tax from Schedule 8812. This typically applies if you received advance Child Tax Credit payments in excess of your allowed credit.">
                    <HelpCircle size={16} />
                  </div>
                </div>
                <input 
                  type="number" 
                  name="additionalTax8812" 
                  value={data.additionalTax8812 ?? ''} 
                  onChange={onDataChange} 
                  placeholder="Line 19" 
                />
              </div>

              <div className="form-group">
                <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                  <label>Section 965 Installment</label>
                  <div className="has-tooltip header-help-icon" data-tooltip="Enter the net tax liability installment from Form 965-A (Repatriation tax).">
                    <HelpCircle size={16} />
                  </div>
                </div>
                <input 
                  type="number" 
                  name="section965TaxInstallment" 
                  value={data.section965TaxInstallment ?? ''} 
                  onChange={onDataChange} 
                  placeholder="Line 20" 
                />
              </div>
            </div>

            {/* Display the Calculated Total for Part II */}
            <div className="totals-summary-box" style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '0.5rem' }}>
              <div className="form-group">
                <label style={{ color: '#166534', fontWeight: 'bold' }}>Total Additional Taxes (Schedule 2, Line 21)</label>
                <input 
                  type="number" 
                  value={totals.sched2_line21 ?? ''} 
                  readOnly 
                  style={{ backgroundColor: '#fff', fontWeight: 'bold', color: '#166534', borderColor: '#86efac' }} 
                />
                <small style={{ color: '#15803d' }}>This amount is added to your Form 1040, Line 23.</small>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* =================================================================================
          PART 3: ADDITIONAL CREDITS (Schedule 3)
         ================================================================================= */}
      
      <div style={{ marginTop: '2rem', borderTop: '3px solid #f0e7e2ff', paddingTop: '1rem' }}>
        <div className="content-box-header" style={{ marginBottom: '1rem' }}>
          <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <DollarSign size={18} className="icon-orange" /> Additional Credits
          </h4>
        </div>

        <div className="form-group" style={{ backgroundColor: '#fff7ed', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #fed7aa' }}>
          <label style={{ fontSize: '1rem', fontWeight: '600', color: '#9a3412', marginBottom: '0.75rem', display: 'block' }}>
            Do you have any other credits or payments?
          </label>
          <p style={{ fontSize: '0.9rem', color: '#6e400cff', marginBottom: '1rem' }}>
            This includes Foreign Tax Credit, Residential Energy credits, General Business Credit, or Excess Social Security tax withheld.
          </p>
          
          <div className="radio-group">
            <input type="radio" id="sched3_no" name="hasSchedule3" value="false" checked={data.hasSchedule3 === false} onChange={handleToggleChange} />
            <label htmlFor="sched3_no">No</label>
            <input type="radio" id="sched3_yes" name="hasSchedule3" value="true" checked={data.hasSchedule3 === true} onChange={handleToggleChange} />
            <label htmlFor="sched3_yes">Yes</label>
          </div>
        </div>

        {data.hasSchedule3 && (
          <div className="dependent-card" style={{ marginTop: '1rem', animation: 'fadeIn 0.3s' }}>
            <h5 style={{ margin: '0 0 1rem 0', fontSize: '0.95rem', color: '#695e47ff' }}>Part I: Nonrefundable Credits</h5>
            
            <div className="form-row">
              <div className="form-group">
                <label>Foreign Tax Credit (Form 1116)</label>
                <input type="number" name="foreignTaxCredit" value={data.foreignTaxCredit ?? ''} onChange={onDataChange} />
              </div>
              <div className="form-group">
                <label>Residential Clean Energy (Form 5695)</label>
                <input type="number" name="residentialCleanEnergyCredit" value={data.residentialCleanEnergyCredit ?? ''} onChange={onDataChange} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Energy Efficient Home Improvement</label>
                <input type="number" name="energyEfficientHomeCredit" value={data.energyEfficientHomeCredit ?? ''} onChange={onDataChange} />
              </div>
              <div className="form-group">
                <label>Clean Vehicle Credit (Form 8936)</label>
                <input type="number" name="cleanVehicleCredit" value={data.cleanVehicleCredit ?? ''} onChange={onDataChange} />
              </div>
            </div>

            {/* --- NEW: Education Credit (Nonrefundable) --- */}
            <div className="form-row">
              <div className="form-group">
                <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                  <label>Education Credits (Nonrefundable)</label>
                  <div className="has-tooltip header-help-icon" data-tooltip="Enter the nonrefundable portion from Form 8863, line 19.">
                    <HelpCircle size={16} />
                  </div>
                </div>
                <input type="number" name="educationCreditNonRefundable" value={data.educationCreditNonRefundable ?? ''} onChange={onDataChange} placeholder="Schedule 3, Line 3" />
              </div>
            </div>

            {/* --- NEW: Lines 6h - 6l --- */}
            <div className="form-row">
              <div className="form-group">
                <label>DC Homebuyer Credit (Form 8859)</label>
                <input type="number" name="dcHomebuyerCredit" value={data.dcHomebuyerCredit ?? ''} onChange={onDataChange} placeholder="Line 6h" />
              </div>
              <div className="form-group">
                <label>Qualified Electric Vehicle (Form 8834)</label>
                <input type="number" name="qualifiedElectricVehicleCredit" value={data.qualifiedElectricVehicleCredit ?? ''} onChange={onDataChange} placeholder="Line 6i" />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Alt. Fuel Vehicle Refueling (Form 8911)</label>
                <input type="number" name="alternativeFuelVehicleCredit" value={data.alternativeFuelVehicleCredit ?? ''} onChange={onDataChange} placeholder="Line 6j" />
              </div>
              <div className="form-group">
                <label>Tax Credit Bonds (Form 8912)</label>
                <input type="number" name="taxCreditBonds" value={data.taxCreditBonds ?? ''} onChange={onDataChange} placeholder="Line 6k" />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Form 8978 Credit (Line 6l)</label>
                <input type="number" name="form8978Credit" value={data.form8978Credit ?? ''} onChange={onDataChange} />
              </div>
            </div>

            {/* --- Line 6z: Other Nonrefundable Credits --- */}
            <div className="dependent-card" style={{ marginTop: '1rem', backgroundColor: '#fff', border: '1px solid #f0eae2ff' }}>
              <h5 style={{ margin: '0 0 1rem 0', fontSize: '0.95rem', color: '#695b47ff' }}>Line 6z: Other Nonrefundable Credits</h5>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Description</label>
                  <input 
                    type="text" 
                    name="line6zDescription" 
                    value={data.line6zDescription ?? ''} 
                    onChange={onDataChange} 
                    placeholder="e.g. Form 8910" 
                  />
                </div>
                <div className="form-group">
                  <label>Amount</label>
                  <input 
                    type="number" 
                    name="line6zAmount" 
                    value={data.line6zAmount ?? ''} 
                    onChange={onDataChange} 
                    placeholder="0.00" 
                  />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>General Business Credit (Form 3800)</label>
                <input type="number" name="generalBusinessCredit" value={data.generalBusinessCredit ?? ''} onChange={onDataChange} />
              </div>
              <div className="form-group">
                <label>Prior Year Minimum Tax Credit (Form 8801)</label>
                <input type="number" name="priorYearMinTaxCredit" value={data.priorYearMinTaxCredit ?? ''} onChange={onDataChange} />
              </div>
            </div>

            <h5 style={{ margin: '1.5rem 0 1rem 0', fontSize: '0.95rem', color: '#695247ff', borderTop: '1px dashed #f0e8e2ff', paddingTop: '1rem' }}>Part II: Refundable Credits</h5>

            <div className="form-row">
              <div className="form-group">
                <label>Amount Paid with Extension</label>
                <input type="number" name="amountPaidWithExtension" value={data.amountPaidWithExtension ?? ''} onChange={onDataChange} />
              </div>
              <div className="form-group">
                <label>Excess Social Security Tax</label>
                <input type="number" name="excessSocialSecurityTax" value={data.excessSocialSecurityTax ?? ''} onChange={onDataChange} />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Credit for Federal Tax on Fuels</label>
                <input type="number" name="fuelTaxCredit" value={data.fuelTaxCredit ?? ''} onChange={onDataChange} />
              </div>
            </div>

            {/* --- NEW: Lines 13a - 13d --- */}
            <div className="form-row">
              <div className="form-group">
                <label>Form 2439 (Undistributed Cap Gains)</label>
                <input type="number" name="form2439Payment" value={data.form2439Payment ?? ''} onChange={onDataChange} placeholder="Line 13a" />
              </div>
              <div className="form-group">
                <label>Section 1341 Credit (Repayment)</label>
                <input type="number" name="section1341Credit" value={data.section1341Credit ?? ''} onChange={onDataChange} placeholder="Line 13b" />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Elective Payment (Form 3800)</label>
                <input type="number" name="electivePaymentElection" value={data.electivePaymentElection ?? ''} onChange={onDataChange} placeholder="Line 13c" />
              </div>
              <div className="form-group">
                <label>Deferred Net 965 Liability</label>
                <input type="number" name="deferred965Liability" value={data.deferred965Liability ?? ''} onChange={onDataChange} placeholder="Line 13d" />
              </div>
            </div>
            {/* --- Line 13z: Other Refundable Credits --- */}
            <div className="dependent-card" style={{ marginTop: '1rem', backgroundColor: '#fff', border: '1px solid #f0e7e2ff' }}>
              <h5 style={{ margin: '0 0 1rem 0', fontSize: '0.95rem', color: '#695547ff' }}>Line 13z: Other Refundable Credits</h5>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Description</label>
                  <input 
                    type="text" 
                    name="line13zDescription" 
                    value={data.line13zDescription ?? ''} 
                    onChange={onDataChange} 
                    placeholder="e.g. 960(c) or Form 8689" 
                  />
                </div>
                <div className="form-group">
                  <label>Amount</label>
                  <input 
                    type="number" 
                    name="line13zAmount" 
                    value={data.line13zAmount ?? ''} 
                    onChange={onDataChange} 
                    placeholder="0.00" 
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* =================================================================================
          PART 4: REFUNDABLE CREDITS (Form 1040 Lines 27-31)
         ================================================================================= */}
      
      <div style={{ marginTop: '2rem', borderTop: '3px solid #f0e6e2ff', paddingTop: '1.5rem' }}>
        <div className="content-box-header" style={{ marginBottom: '1rem' }}>
          <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <CheckCircle size={18} className="icon-orange" /> Refundable Credits
          </h4>
        </div>

        <div className="dependent-card">
          
          {/* --- Line 27: Earned Income Credit --- */}
          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
              <label>Earned Income Credit (Line 27)</label>
              <div className="has-tooltip header-help-icon" data-tooltip="Enter the amount from the EIC Worksheet.">
                <HelpCircle size={16} />
              </div>
            </div>
            <input 
              type="number" 
              name="earnedIncomeCredit" 
              value={data.earnedIncomeCredit ?? ''} 
              onChange={onDataChange} 
              placeholder="Line 27a" 
            />
            
            {/* Line 27b: Clergy */}
            <div className="checkbox-group" style={{ marginTop: '0.5rem' }}>
                <input 
                    type="checkbox" 
                    id="clergyFilingSE" 
                    name="clergyFilingSE" 
                    checked={data.clergyFilingSE || false} 
                    onChange={onDataChange} 
                />
                <label htmlFor="clergyFilingSE">
                    <strong>27b.</strong> Check if you are Clergy filing Schedule SE (and line 2 includes 1040 line 1z)
                </label>
            </div>

            {/* Line 27c: Do Not Claim */}
            <div className="checkbox-group" style={{ marginTop: '0.5rem' }}>
                <input 
                    type="checkbox" 
                    id="doNotClaimEIC" 
                    name="doNotClaimEIC" 
                    checked={data.doNotClaimEIC || false} 
                    onChange={onDataChange} 
                />
                <label htmlFor="doNotClaimEIC">
                    <strong>27c.</strong> Check here if you do <strong>not</strong> want to claim the EIC (or are not eligible)
                </label>
            </div>
          </div>

          {/* --- Line 28: Additional Child Tax Credit --- */}
          <div className="form-group" style={{ marginBottom: '1.5rem', borderTop: '1px dashed #e2e8f0', paddingTop: '1rem' }}>
            <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
              <label>Additional Child Tax Credit (Line 28)</label>
              <div className="has-tooltip header-help-icon" data-tooltip="This is calculated automatically based on your dependents, but you can override it here if needed (Form 8812).">
                <HelpCircle size={16} />
              </div>
            </div>
            <input 
              type="number" 
              name="manualAdditionalChildTaxCredit" 
              value={data.manualAdditionalChildTaxCredit ?? ''} 
              onChange={onDataChange} 
              placeholder="Auto-calculated" 
            />
            
            {/* Line 28 Checkbox */}
            <div className="checkbox-group" style={{ marginTop: '0.5rem' }}>
              <input 
                  type="checkbox" 
                  id="line28Form8862" 
                  name="line28Form8862" 
                  checked={data.line28Form8862 || false} 
                  onChange={onDataChange} 
              />
              <label htmlFor="line28Form8862" style={{ fontSize: '0.85rem', color: '#666' }}>
                  Check if you must file <strong>Form 8862</strong> (Credit was previously disallowed)
              </label>
            </div>
          </div>

          {/* --- Line 29: American Opportunity Credit --- */}
          <div className="form-group" style={{ marginBottom: '1.5rem', borderTop: '1px dashed #e2e8f0', paddingTop: '1rem' }}>
            <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
              <label>American Opportunity Credit (Line 29)</label>
              <div className="has-tooltip header-help-icon" data-tooltip="Enter the REFUNDABLE portion from Form 8863, Line 8.">
                <HelpCircle size={16} />
              </div>
            </div>
            <input 
              type="number" 
              name="refundableEducationCredit" 
              value={data.refundableEducationCredit ?? ''} 
              onChange={onDataChange} 
              placeholder="Line 29" 
            />
          </div>

          {/* --- Line 31: Net Premium Tax Credit --- */}
          <div className="form-group" style={{ marginBottom: '1.5rem', borderTop: '1px dashed #e2e8f0', paddingTop: '1rem' }}>
            <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
              <label>Net Premium Tax Credit (Line 31)</label>
              <div className="has-tooltip header-help-icon" data-tooltip="Enter the amount from Form 8962, Line 26.">
                <HelpCircle size={16} />
              </div>
            </div>
            <input 
              type="number" 
              name="netPremiumTaxCredit" 
              value={data.netPremiumTaxCredit ?? ''} 
              onChange={onDataChange} 
              placeholder="Line 31" 
            />
          </div>

        </div>
      </div>

      {/* =================================================================================
          PART 5: PAYMENTS (Line 26)
         ================================================================================= */}
      
      <div style={{ marginTop: '2rem', borderTop: '3px solid #f0e6e2ff', paddingTop: '1.5rem' }}>
        <div className="content-box-header" style={{ marginBottom: '1rem' }}>
          <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <DollarSign size={18} className="icon-orange" /> Estimated Tax Payments
          </h4>
        </div>

        <div className="dependent-card">
          <div className="form-row">
            <div className="form-group">
              <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                <label>2024 Estimated Tax Payments</label>
                <div className="has-tooltip header-help-icon" data-tooltip="Enter the total amount of estimated tax payments you made for 2024 (Form 1040-ES), plus any overpayment applied from your 2023 return.">
                  <HelpCircle size={16} />
                </div>
              </div>
              <input 
                type="number" 
                name="estimatedTaxPayments" 
                value={data.estimatedTaxPayments ?? ''} 
                onChange={onDataChange} 
                placeholder="Line 26" 
              />
            </div>
            
            {/* --- NEW: SSN Field for Line 26 --- */}
            <div className="form-group">
              <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
                <label>Spouse's SSN (If filing separate)</label>
                <div className="has-tooltip header-help-icon" data-tooltip="Required ONLY if you made joint estimated payments but are now filing separately (or were divorced in 2024).">
                  <HelpCircle size={16} />
                </div>
              </div>
              <input 
                type="text" 
                name="formerSpouseSSN" 
                value={data.formerSpouseSSN ?? ''} 
                onChange={onDataChange} 
                placeholder="XXX-XX-XXXX" 
              />
            </div>
          </div>
        </div>
      </div>

      {/* =================================================================================
          PART 5: SAFE HARBOR
         ================================================================================= */}
      
      <div style={{ marginTop: '2rem', borderTop: '1px solid #f0eae2ff', paddingTop: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
            <AlertTriangle size={18} style={{ color: '#f59e0b', marginRight: '0.5rem' }} />
            <h3 style={{ fontSize: '1.1rem', margin: 0, fontWeight: 600 }}>Underpayment Penalty Safe Harbor</h3>
        </div>
        <p className="helper-text" style={{ marginBottom: '1rem', color: '#8b7764ff', fontSize: '0.9rem' }}>
            To avoid an underpayment penalty, the IRS checks if you paid 100% of last year's tax.
            Please enter the "Total Tax" from your 2023 Form 1040 (Line 24).
        </p>
        
        <div className="form-row">
            <div className="form-group">
                <label>2023 Total Tax (Form 1040, Line 24)</label>
                <div className="input-with-prefix">
                    <span className="prefix">$</span>
                    <input
                        type="number"
                        name="priorYearTax"
                        value={data.priorYearTax ?? ''}
                        onChange={onDataChange}
                        placeholder="0.00"
                    />
                </div>
            </div>
        </div>
      </div>

    </div>
  );
}

export default CreditsAndAdjustments;